import { useEffect, useState } from "react";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import { Overline } from "@/components/ui-bits";
import { toast } from "sonner";
import {
  Radar, Loader2, Plug, Trash2, Settings2, Check, Zap, ExternalLink, Gavel, TriangleAlert, Inbox, FlaskConical, PackageCheck, MessageSquare, Crown, ShieldCheck, Rocket, TrendingUp, Trophy, Lightbulb, BellRing, Send, Wand2, Coins,
} from "lucide-react";

const NICHE_SUGGESTIONS = [
  "shopify store", "webflow site", "ai chatbot", "next.js website", "wordpress site",
  "saas dashboard", "landing page", "wix site", "squarespace site", "api integration",
  "web scraping", "mobile app", "ui ux design", "seo website", "automation script",
];

export default function LeadSources() {
  const [cfg, setCfg] = useState(null);
  const [bids, setBids] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [winrate, setWinrate] = useState(null);
  const [costwin, setCostwin] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([
      api.get("/leadsources/config").then((r) => setCfg(r.data)),
      api.get("/leadsources/bids").then((r) => setBids(r.data)),
      api.get("/leadsources/deliveries").then((r) => setDeliveries(r.data)),
      api.get("/leadsources/sponsors").then((r) => setSponsors(r.data)).catch(() => {}),
      api.get("/leadsources/winrate").then((r) => setWinrate(r.data)).catch(() => {}),
      api.get("/leadsources/cost-per-win").then((r) => setCostwin(r.data)).catch(() => {}),
      api.get("/alerts/config").then((r) => setAlerts(r.data)).catch(() => {}),
    ]).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useRealtime(() => load());

  if (loading && !cfg) {
    return <div className="hud-card rounded-lg p-16 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="leadsources-page">
      <div>
        <Overline>Autonomous Lead Sourcing</Overline>
        <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Lead Sources</h1>
        <p className="text-sm text-slate-400 mt-1 max-w-2xl">Connect Freelancer.com and let the Prospector agent scout active software projects and auto-submit tailored bids — inside a daily bid cap you control.</p>
      </div>

      {!cfg?.marketer_agent && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="no-agent-warn">
          <TriangleAlert className="w-4 h-4 text-amber-400 mt-0.5" />
          <div className="text-sm text-slate-300">No active agent to run bids. Create a <span className="text-sky-400 font-medium">Marketer</span> or Seller in <a href="/agents" className="text-cyan-400 underline">Agents Fleet</a>.</div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <FreelancerConnect cfg={cfg} onChange={load} />
        <ProspectPolicy cfg={cfg} onChange={load} />
      </div>

      <AwardAlerts alerts={alerts} enabled={cfg?.settings?.award_alerts_enabled} onChange={load} />

      {deliveries.length > 0 && <DeliveryList deliveries={deliveries} />}
      {sponsors.length > 0 && <SponsorApprovals sponsors={sponsors} onChange={load} />}
      <CostPerWin data={costwin} membership={cfg?.settings?.freelancer_membership_cost} onChange={load} />
      <WinRate data={winrate} />
      <BidList bids={bids} />
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      <input {...props} className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none" />
    </div>
  );
}

function FreelancerConnect({ cfg, onChange }) {
  const connected = cfg?.freelancer?.connected;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ access_token: "", user_id: "" });
  const [busy, setBusy] = useState(false);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.post("/leadsources/connect/freelancer", { access_token: f.access_token, user_id: Number(f.user_id) });
      toast.success(`Freelancer connected: ${r.data.display_name || "verified"}`);
      setOpen(false); onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not connect Freelancer");
    } finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Freelancer?")) return;
    await api.delete("/leadsources/disconnect/freelancer"); toast.success("Disconnected"); onChange();
  };

  return (
    <div className="hud-card rounded-lg p-5" data-testid="freelancer-connect-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#29B2FE]" />
          <span className="font-display font-bold text-[#29B2FE]">Freelancer.com</span>
          <span className="text-[11px] text-slate-500">Projects + auto-bid</span>
        </div>
        {connected ? <span className="px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono text-[10px]">CONNECTED</span>
          : <span className="px-2 py-0.5 rounded bg-slate-700/40 text-slate-400 font-mono text-[10px]">NOT CONNECTED</span>}
      </div>

      {connected ? (
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Account</span><span className="text-slate-200">{cfg.freelancer.display_name || cfg.freelancer.user_id}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">User ID</span><span className="text-slate-400 font-mono text-xs">{cfg.freelancer.user_id}</span></div>
          <button data-testid="freelancer-disconnect-btn" onClick={disconnect} className="mt-3 flex items-center gap-1.5 text-[11px] font-mono text-red-400 hover:text-red-300"><Trash2 className="w-3 h-3" /> Disconnect</button>
        </div>
      ) : !open ? (
        <button data-testid="freelancer-connect-btn" onClick={() => setOpen(true)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md bg-sky-500/15 border border-sky-500/40 text-sky-300 font-mono text-xs uppercase tracking-wider hover:bg-sky-500/25 transition-colors"><Plug className="w-4 h-4" /> Connect Freelancer</button>
      ) : (
        <div className="space-y-2.5">
          <Field label="OAuth Access Token" data-testid="freelancer-token-input" value={f.access_token} onChange={(e) => setF({ ...f, access_token: e.target.value })} placeholder="accounts.freelancer.com/settings/develop → Generate Token" />
          <Field label="User ID (optional — auto-detected from your token)" data-testid="freelancer-userid-input" value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })} placeholder="leave blank to auto-detect" />
          <p className="text-[10px] text-slate-500">Get it at <span className="text-sky-400">accounts.freelancer.com/settings/develop</span> → Application Dashboard → Generate Token. Stored per-workspace, server-side only; we verify it and auto-detect your user ID before saving.</p>
          <div className="flex gap-2">
            <button data-testid="freelancer-connect-submit" onClick={connect} disabled={busy || !f.access_token} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-md bg-sky-500 text-[#080B11] font-bold text-sm hover:bg-sky-400 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Verify &amp; connect
            </button>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-md border border-slate-700 text-slate-400 text-sm">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Toggle({ on, onChange, label, sub, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!on)} className="flex items-start gap-3 text-left w-full">
      <span className={`mt-0.5 w-9 h-5 rounded-full flex items-center px-0.5 transition-colors shrink-0 ${on ? "bg-emerald-500" : "bg-slate-700"}`}>
        <span className={`w-4 h-4 rounded-full bg-white transition-transform ${on ? "translate-x-4" : ""}`} />
      </span>
      <span>
        <span className="block text-sm text-slate-200 font-medium">{label}</span>
        <span className="block text-[10px] text-slate-500">{sub}</span>
      </span>
    </button>
  );
}

function runReasonLabel(reason) {
  return {
    freelancer_not_connected: "Freelancer not connected",
    daily_cap_reached: "Daily bid cap reached — resets tomorrow",
    monthly_cap_reached: "Monthly bid budget used up",
    no_projects_found: "No projects matched your keywords + min budget",
    search_failed: "Freelancer search failed",
  }[reason] || reason;
}

function runSummaryText(lr) {
  if (!lr) return "No runs yet";
  if (lr.reason) return lr.last_error ? `${runReasonLabel(lr.reason)}: ${lr.last_error}` : runReasonLabel(lr.reason);
  const s = lr.skipped || {};
  const parts = [];
  if (s.already_bid) parts.push(`${s.already_bid} already-bid`);
  if (s.unverified) parts.push(`${s.unverified} unverified`);
  if (s.low_score) parts.push(`${s.low_score} low-score`);
  if (s.saturated) parts.push(`${s.saturated} saturated`);
  if (s.no_price) parts.push(`${s.no_price} no-price`);
  let txt = `Scanned ${lr.scanned ?? 0} · bid ${lr.bid ?? 0}` + (parts.length ? ` · skipped ${parts.join(", ")}` : "");
  if (lr.last_error) txt += ` · error: ${lr.last_error}`;
  return txt;
}

function runSummaryTone(lr) {
  if (!lr) return "info";
  if (lr.last_error || lr.reason === "search_failed" || lr.reason === "freelancer_not_connected") return "error";
  if (lr.bid > 0) return "success";
  return "info";
}

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso), s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function LastRunPanel({ run }) {
  if (!run) return (
    <div className="mt-3 rounded-md border border-slate-800 bg-slate-900/30 px-3 py-2.5 text-[11px] font-mono text-slate-500" data-testid="last-run-panel">
      No prospect run recorded yet — click <span className="text-emerald-400">Prospect now</span> to see a live breakdown.
    </div>
  );
  const s = run.skipped || {};
  const chips = [
    ["already-bid", s.already_bid], ["unverified", s.unverified], ["low-score", s.low_score],
    ["saturated", s.saturated], ["no-price", s.no_price],
  ].filter(([, v]) => v > 0);
  return (
    <div className="mt-3 rounded-md border border-slate-800 bg-slate-900/30 p-3" data-testid="last-run-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5"><Radar className="w-3.5 h-3.5 text-cyan-400" /><span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Last prospect run</span></div>
        <span className="text-[10px] font-mono text-slate-500">{relTime(run.at)}</span>
      </div>
      <div className="flex items-center gap-3 flex-wrap text-[12px]">
        <span className="font-mono text-slate-300">scanned <b className="text-slate-100">{run.scanned ?? 0}</b></span>
        <span className={`font-mono ${run.bid > 0 ? "text-emerald-400" : "text-slate-400"}`}>bid <b>{run.bid ?? 0}</b></span>
        {run.reason && <span className="px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300 font-mono text-[10px]">{runReasonLabel(run.reason)}</span>}
      </div>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {chips.map(([k, v]) => (
            <span key={k} className="px-1.5 py-0.5 rounded bg-slate-800/60 border border-slate-700 text-slate-400 font-mono text-[10px]" data-testid={`skip-${k}`}>{v} {k}</span>
          ))}
        </div>
      )}
      {run.last_error && (
        <div className="mt-2 flex items-start gap-1.5 text-[10px] text-red-300/90 bg-red-500/5 border border-red-500/20 rounded px-2.5 py-2" data-testid="last-run-error">
          <TriangleAlert className="w-3 h-3 mt-0.5 shrink-0" />
          <span className="font-mono break-all">Freelancer error: {run.last_error}</span>
        </div>
      )}
      {!run.reason && !run.last_error && run.bid === 0 && (run.scanned ?? 0) > 0 && (
        <p className="text-[10px] text-slate-500 mt-2">Projects were found but all filtered out. Loosen the <b className="text-slate-400">Rating Shield</b> or the <b className="text-slate-400">Easy-Wins score</b> below to widen the pool.</p>
      )}
    </div>
  );
}

function ProspectPolicy({ cfg, onChange }) {
  const s = cfg?.settings || {};
  const [enabled, setEnabled] = useState(!!s.prospect_enabled);
  const [cap, setCap] = useState(s.prospect_daily_bid_cap ?? 5);
  const [kw, setKw] = useState(s.prospect_keywords ?? "");
  const [minB, setMinB] = useState(s.prospect_min_budget ?? 250);
  const [pricing, setPricing] = useState(s.prospect_pricing ?? "competitive");
  const [undercut, setUndercut] = useState(s.prospect_undercut_pct ?? 5);
  const [clientUpd, setClientUpd] = useState(s.client_update_enabled !== false);
  const [autoBuild, setAutoBuild] = useState(!!s.auto_build_on_award);
  const [easyWins, setEasyWins] = useState(s.prospect_easy_wins !== false);
  const [minScore, setMinScore] = useState(s.prospect_min_score ?? 55);
  const [reqVerified, setReqVerified] = useState(s.prospect_require_verified !== false);
  const [maxComp, setMaxComp] = useState(s.prospect_max_competitors ?? 25);
  const [monthlyCap, setMonthlyCap] = useState(s.prospect_monthly_bid_cap ?? 1500);
  const [perRun, setPerRun] = useState(s.prospect_per_run_cap ?? 3);
  const [rapid, setRapid] = useState(s.rapid_response_enabled !== false);
  const [sponsor, setSponsor] = useState(!!s.sponsor_enabled);
  const [sponsorMin, setSponsorMin] = useState(s.sponsor_min_budget ?? 500);
  const [insightAuto, setInsightAuto] = useState(!!s.insight_autopilot);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [updating, setUpdating] = useState(false);
  useEffect(() => {
    const x = cfg?.settings || {};
    setEnabled(!!x.prospect_enabled); setCap(x.prospect_daily_bid_cap ?? 5);
    setKw(x.prospect_keywords ?? ""); setMinB(x.prospect_min_budget ?? 250);
    setPricing(x.prospect_pricing ?? "competitive"); setUndercut(x.prospect_undercut_pct ?? 5);
    setClientUpd(x.client_update_enabled !== false); setAutoBuild(!!x.auto_build_on_award);
    setEasyWins(x.prospect_easy_wins !== false); setReqVerified(x.prospect_require_verified !== false);
    setMinScore(x.prospect_min_score ?? 55);
    setMaxComp(x.prospect_max_competitors ?? 25); setMonthlyCap(x.prospect_monthly_bid_cap ?? 1500);
    setPerRun(x.prospect_per_run_cap ?? 3); setRapid(x.rapid_response_enabled !== false);
    setSponsor(!!x.sponsor_enabled); setSponsorMin(x.sponsor_min_budget ?? 500);
    setInsightAuto(!!x.insight_autopilot);
  }, [cfg]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/leadsources/settings", {
        prospect_enabled: enabled, prospect_daily_bid_cap: Number(cap),
        prospect_keywords: kw, prospect_min_budget: Number(minB),
        prospect_pricing: pricing, prospect_undercut_pct: Number(undercut),
        client_update_enabled: clientUpd, auto_build_on_award: autoBuild,
        prospect_easy_wins: easyWins, prospect_require_verified: reqVerified,
        prospect_min_score: Number(minScore),
        prospect_max_competitors: Number(maxComp), prospect_monthly_bid_cap: Number(monthlyCap),
        prospect_per_run_cap: Number(perRun), rapid_response_enabled: rapid,
        sponsor_enabled: sponsor, sponsor_min_budget: Number(sponsorMin),
        insight_autopilot: insightAuto,
      });
      toast.success("Prospector policy saved"); onChange();
    } catch { toast.error("Could not save"); } finally { setBusy(false); }
  };

  const runNow = async () => {
    setRunning(true);
    const prevAt = cfg?.last_run?.at || null;
    try {
      const r = await api.post("/leadsources/prospect/run");
      if (!r.data?.started) { toast.info("Prospector is already running — hang tight"); setRunning(false); return; }
      toast.success("Prospector started — scanning projects…");
      let tries = 0;
      const poll = setInterval(async () => {
        tries++;
        try {
          const { data } = await api.get("/leadsources/config");
          const lr = data?.last_run;
          if (lr && lr.at && lr.at !== prevAt) {
            clearInterval(poll); setRunning(false);
            toast[runSummaryTone(lr)](runSummaryText(lr), { duration: 9000 });
            onChange();
            return;
          }
        } catch { /* keep polling */ }
        if (tries >= 8) { clearInterval(poll); setRunning(false); onChange(); }
      }, 3000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Prospect run failed");
      setRunning(false);
    }
  };

  const syncInbox = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/leadsources/inbox/sync");
      toast[r.data?.started ? "success" : "info"](r.data?.started ? "Syncing awards & replies from Freelancer…" : "A sync is already running");
      setTimeout(onChange, 4000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const postUpdates = async () => {
    setUpdating(true);
    try {
      const r = await api.post("/leadsources/updates/run");
      toast[r.data?.started ? "success" : "info"](r.data?.started ? "Posting client updates to Freelancer…" : "Updates are already being posted");
      setTimeout(onChange, 4000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update run failed"); }
    finally { setUpdating(false); }
  };

  return (
    <div className="hud-card rounded-lg p-5" data-testid="prospect-policy-card">
      <div className="flex items-center gap-2 mb-3"><Settings2 className="w-4 h-4 text-cyan-400" /><span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">Prospector policy</span></div>
      <Toggle testid="prospect-toggle" on={enabled} onChange={setEnabled} label="Autonomous bidding"
              sub="The Prospector agent scouts active projects and submits tailored bids on its own, up to the daily cap." />
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Daily bid cap</label>
          <input data-testid="prospect-cap-input" type="number" min="0" max="50" value={cap} onChange={(e) => setCap(e.target.value)} className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none" />
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Min project budget</label>
          <div className="flex items-center gap-1"><span className="text-slate-500">$</span><input data-testid="prospect-minbudget-input" type="number" min="0" value={minB} onChange={(e) => setMinB(e.target.value)} className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none" /></div>
        </div>
      </div>
      <div className="mt-3">
        <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Search keywords</label>
        <input data-testid="prospect-keywords-input" value={kw} onChange={(e) => setKw(e.target.value)} placeholder="website design, landing page, react web app, python fastapi api" className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none" />
        <p className="text-[10px] text-slate-500 mt-1">Separate with commas to widen the net — each group runs its own search and results are merged (up to 6 groups).</p>
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="niche-suggestions">
          {NICHE_SUGGESTIONS.map((n) => {
            const already = kw.toLowerCase().split(",").map((x) => x.trim()).includes(n);
            return (
              <button key={n} type="button" data-testid={`niche-${n.replace(/\s+/g, "-")}`} disabled={already}
                onClick={() => setKw(kw.trim() ? `${kw.replace(/,\s*$/, "")}, ${n}` : n)}
                className={`px-2 py-0.5 rounded-full text-[10px] font-mono border transition-colors ${already ? "bg-emerald-500/10 text-emerald-500/70 border-emerald-500/20 cursor-default" : "bg-slate-800/40 text-slate-400 border-slate-700 hover:border-cyan-500/50 hover:text-cyan-300"}`}>
                {already ? "✓ " : "+ "}{n}
              </button>
            );
          })}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Bid pricing</label>
          <select data-testid="prospect-pricing-select" value={pricing} onChange={(e) => setPricing(e.target.value)} className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none">
            <option value="competitive">Competitive — undercut the average bid</option>
            <option value="value">Value — budget midpoint</option>
            <option value="floor">Floor — minimum budget</option>
          </select>
        </div>
        <div className={pricing === "competitive" ? "" : "opacity-50 pointer-events-none"}>
          <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Undercut average by (%)</label>
          <input data-testid="prospect-undercut-input" type="number" min="0" max="40" value={undercut} onChange={(e) => setUndercut(e.target.value)} className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none" />
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button data-testid="prospect-save" onClick={save} disabled={busy} className="flex items-center gap-2 px-4 py-2 rounded-md bg-cyan-500 text-[#080B11] font-bold text-sm hover:bg-cyan-400 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save policy
        </button>
        <button data-testid="prospect-run-btn" onClick={runNow} disabled={running || !cfg?.freelancer?.connected} title={cfg?.freelancer?.connected ? "" : "Connect Freelancer first"} className="flex items-center gap-2 px-4 py-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 font-mono text-xs uppercase tracking-wider hover:bg-emerald-500/20 disabled:opacity-40">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Prospect now
        </button>
        <button data-testid="inbox-sync-btn" onClick={syncInbox} disabled={syncing || !cfg?.freelancer?.connected} title={cfg?.freelancer?.connected ? "" : "Connect Freelancer first"} className="flex items-center gap-2 px-4 py-2 rounded-md border border-sky-500/40 bg-sky-500/10 text-sky-300 font-mono text-xs uppercase tracking-wider hover:bg-sky-500/20 disabled:opacity-40">
          {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Inbox className="w-4 h-4" />} Sync replies
        </button>
        <span className="text-[11px] font-mono text-slate-500">{cfg?.bids_today ?? 0} today · {cfg?.bids_month ?? 0}/{monthlyCap || "∞"} this month · {cfg?.total_bids ?? 0} total</span>
      </div>
      <LastRunPanel run={cfg?.last_run} />
      <div className="mt-3 pt-3 border-t border-slate-800/60">
        <Toggle testid="client-update-toggle" on={clientUpd} onChange={setClientUpd} label="Client Update Autopilot"
                sub="The Builder posts progress updates to the client's Freelancer thread automatically (up to 4, daily)." />
        <button type="button" data-testid="client-update-run-btn" onClick={postUpdates} disabled={updating || !cfg?.freelancer?.connected} title={cfg?.freelancer?.connected ? "" : "Connect Freelancer first"} className="mt-2 flex items-center gap-1.5 text-[11px] font-mono text-sky-300 hover:text-sky-200 disabled:opacity-40">
          {updating ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3 h-3" />} Post client updates now
        </button>
      </div>
      <div className="mt-3 pt-3 border-t border-slate-800/60">
        <Toggle testid="autobuild-toggle" on={autoBuild} onChange={setAutoBuild} label="Auto-Build on Award"
                sub="When a bid is awarded, Forge starts building & shipping automatically (capped daily; still respects your deploy-approval gate)." />
      </div>
      <div className="mt-3 pt-3 border-t border-slate-800/60">
        <div className="flex items-center gap-2 mb-2"><Crown className="w-3.5 h-3.5 text-amber-400" /><span className="font-mono text-[11px] uppercase tracking-wider text-amber-400/90">Easy-Wins Engine · Premier</span></div>
        <p className="text-[10px] text-slate-500 mb-3 leading-relaxed">Wins the projects most likely to say yes — fresh, low-competition, payment-verified — so you rack up quick 5★ reviews first. Bids run every ~5 min prioritizing just-posted projects.</p>
        <div className="space-y-2.5">
          <Toggle testid="easywins-toggle" on={easyWins} onChange={setEasyWins} label="Target easy wins first"
                  sub="Rank projects by winnability and only bid on strong, quick-win opportunities (score-gated)." />
          <Toggle testid="ratingshield-toggle" on={reqVerified} onChange={setReqVerified} label="Rating Shield"
                  sub="Only bid on payment-verified clients — protects your rating from bad projects that hurt ranking for weeks." />
          <Toggle testid="rapid-toggle" on={rapid} onChange={setRapid} label="Rapid-Response Autopilot"
                  sub="Instantly send a smart first reply when a client messages — responsiveness is a direct ranking signal." />
          <Toggle testid="sponsor-toggle" on={sponsor} onChange={setSponsor} label="Auto-Sponsor high-value bids"
                  sub="Queue premium projects for one-tap sponsor sign-off (paid top-of-list visibility). You approve every spend." />
          <Toggle testid="insight-autopilot-toggle" on={insightAuto} onChange={setInsightAuto} label="Insight-Driven Autopilot"
                  sub="Once you have a few awards, the agent boosts projects in your best-converting budget band and favors your winning proposal style — automatically." />
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Max competitors (skip if above)</label>
            <input data-testid="maxcomp-input" type="number" min="0" max="200" value={maxComp} onChange={(e) => setMaxComp(e.target.value)} className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Monthly bid budget (Premier ≈ 1500)</label>
            <input data-testid="monthlycap-input" type="number" min="0" max="1500" value={monthlyCap} onChange={(e) => setMonthlyCap(e.target.value)} className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Bids per run (spread across day)</label>
            <input data-testid="perrun-input" type="number" min="1" max="25" value={perRun} onChange={(e) => setPerRun(e.target.value)} className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none" />
          </div>
          <div className={easyWins ? "" : "opacity-50 pointer-events-none"}>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Easy-Wins min score (lower = more bids)</label>
            <input data-testid="minscore-input" type="number" min="0" max="90" value={minScore} onChange={(e) => setMinScore(e.target.value)} className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none" />
          </div>
          <div className={sponsor ? "" : "opacity-50 pointer-events-none"}>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Sponsor when budget ≥ ($)</label>
            <input data-testid="sponsormin-input" type="number" min="0" value={sponsorMin} onChange={(e) => setSponsorMin(e.target.value)} className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none" />
          </div>
        </div>
      </div>
      <ProposalLearning stats={cfg?.proposal_learning} />
    </div>
  );
}

function WinRate({ data }) {
  if (!data) return null;
  const hasData = data.total_bids > 0;
  const Stat = ({ label, value, accent }) => (
    <div className="rounded-md border border-slate-800 bg-slate-900/40 px-4 py-3">
      <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-display text-2xl font-extrabold mt-0.5 ${accent || "text-slate-100"}`}>{value}</div>
    </div>
  );
  const Bars = ({ title, rows }) => {
    const shown = (rows || []).filter((r) => r.bids > 0);
    if (!shown.length) return null;
    return (
      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">{title}</div>
        <div className="space-y-1.5">
          {shown.map((r) => (
            <div key={r.label} className="flex items-center gap-2" data-testid={`winrate-row-${r.label}`}>
              <span className="text-[11px] text-slate-400 w-24 shrink-0 truncate">{r.label}</span>
              <div className="flex-1 h-4 rounded bg-slate-800/60 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-500/70 to-emerald-400/80 rounded" style={{ width: `${Math.max(r.win_rate, r.awards > 0 ? 4 : 0)}%` }} />
              </div>
              <span className="text-[10px] font-mono text-slate-400 w-28 shrink-0 text-right">{r.win_rate}% · {r.awards}/{r.bids}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };
  return (
    <div className="hud-card rounded-lg p-5" data-testid="winrate-card">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-4 h-4 text-emerald-400" />
        <span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">Win-Rate Dashboard</span>
        {data.best_style && <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded bg-violet-500/10 border border-violet-500/30 text-violet-300 font-mono text-[10px]"><Trophy className="w-3 h-3" /> best style: {data.best_style}</span>}
      </div>
      {!hasData ? (
        <div className="text-center text-slate-500 text-sm py-8">No bids yet. Once the Prospector starts bidding, your win-rate breakdown by budget, proposal style and winnability score appears here.</div>
      ) : (
        <>
          {data.insights?.length > 0 && (
            <div className="mb-5 rounded-md border border-amber-500/25 bg-amber-500/5 p-3.5" data-testid="auto-insights">
              <div className="flex items-center gap-1.5 mb-2"><Lightbulb className="w-3.5 h-3.5 text-amber-400" /><span className="text-[10px] font-mono uppercase tracking-wider text-amber-400/90">Auto-Insights</span></div>
              <ul className="space-y-1.5">
                {data.insights.map((ins, i) => (
                  <li key={i} data-testid={`insight-${ins.kind}`} className="flex items-start gap-2 text-[12px] text-slate-300">
                    <span className="text-amber-400/70 mt-0.5">▸</span><span>{ins.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <Stat label="Total bids" value={data.total_bids} />
            <Stat label="Awarded" value={data.awarded} accent="text-emerald-400" />
            <Stat label="Win rate" value={`${data.win_rate}%`} accent="text-cyan-300" />
            <Stat label="Avg bid" value={`$${data.avg_bid}`} />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <Bars title="By project budget" rows={data.by_budget} />
            <Bars title="By winnability score" rows={data.by_score} />
            <Bars title="By proposal style" rows={data.by_style} />
            <Bars title="By client verification" rows={data.by_verified} />
          </div>
          {data.by_month?.length > 1 && <div className="mt-5"><Bars title="By month" rows={data.by_month} /></div>}
        </>
      )}
    </div>
  );
}

function AwardAlerts({ alerts, enabled, onChange }) {
  const connected = alerts?.telegram?.connected;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ bot_token: "", chat_id: "" });
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.post("/alerts/telegram/connect", f);
      toast.success(`Telegram connected: @${r.data.bot_username || "bot"}`);
      setOpen(false); setF({ bot_token: "", chat_id: "" }); onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not connect Telegram"); }
    finally { setBusy(false); }
  };
  const test = async () => {
    setTesting(true);
    try { await api.post("/alerts/telegram/test"); toast.success("Test alert sent — check Telegram"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test failed"); }
    finally { setTesting(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Telegram alerts?")) return;
    await api.delete("/alerts/telegram/disconnect"); toast.success("Disconnected"); onChange();
  };
  const toggleAlerts = async (on) => {
    try { await api.patch("/leadsources/settings", { award_alerts_enabled: on }); onChange(); }
    catch { toast.error("Could not update"); }
  };

  return (
    <div className="hud-card rounded-lg p-5" data-testid="award-alerts-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BellRing className="w-4 h-4 text-[#29A9EA]" />
          <span className="font-display font-bold text-[#29A9EA]">Award Alerts · Telegram</span>
          <span className="text-[11px] text-slate-500">instant ping when a bid is won</span>
        </div>
        {connected ? <span className="px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono text-[10px]">CONNECTED</span>
          : <span className="px-2 py-0.5 rounded bg-slate-700/40 text-slate-400 font-mono text-[10px]">NOT CONNECTED</span>}
      </div>

      {connected ? (
        <div className="space-y-3">
          <Toggle testid="award-alerts-toggle" on={enabled !== false} onChange={toggleAlerts} label="Send award alerts"
                  sub={`Pinging @${alerts.telegram.bot_username || "your bot"} the moment a Freelancer bid is awarded.`} />
          <div className="flex items-center gap-2">
            <button data-testid="tg-test-btn" onClick={test} disabled={testing} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 text-sky-300 font-mono text-[11px] uppercase tracking-wider hover:bg-sky-500/20 disabled:opacity-50">
              {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send test
            </button>
            <button data-testid="tg-disconnect-btn" onClick={disconnect} className="flex items-center gap-1.5 text-[11px] font-mono text-red-400 hover:text-red-300"><Trash2 className="w-3 h-3" /> Disconnect</button>
          </div>
        </div>
      ) : !open ? (
        <button data-testid="tg-connect-btn" onClick={() => setOpen(true)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md bg-sky-500/15 border border-sky-500/40 text-sky-300 font-mono text-xs uppercase tracking-wider hover:bg-sky-500/25 transition-colors"><Plug className="w-4 h-4" /> Connect Telegram</button>
      ) : (
        <div className="space-y-2.5">
          <Field label="Bot Token (from @BotFather)" data-testid="tg-token-input" type="password" value={f.bot_token} onChange={(e) => setF({ ...f, bot_token: e.target.value })} placeholder="123456:ABC-DEF..." />
          <Field label="Your Chat ID" data-testid="tg-chatid-input" value={f.chat_id} onChange={(e) => setF({ ...f, chat_id: e.target.value })} placeholder="e.g. 123456789" />
          <div className="text-[10px] text-slate-500 leading-relaxed rounded-md bg-slate-900/50 border border-slate-800 p-2.5">
            <b className="text-slate-400">Setup (60 sec):</b> 1) In Telegram, message <span className="text-sky-400">@BotFather</span> → <span className="font-mono">/newbot</span> → copy the token. 2) Send any message to your new bot. 3) Get your chat ID from <span className="text-sky-400">@userinfobot</span> (or @RawDataBot). Paste both above — we send a test message to verify.
          </div>
          <div className="flex gap-2">
            <button data-testid="tg-connect-submit" onClick={connect} disabled={busy || !f.bot_token || !f.chat_id} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-md bg-sky-500 text-[#080B11] font-bold text-sm hover:bg-sky-400 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Verify &amp; connect
            </button>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-md border border-slate-700 text-slate-400 text-sm">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function CostPerWin({ data, membership, onChange }) {
  const [m, setM] = useState(membership ?? 99.95);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setM(membership ?? 99.95); }, [membership]);
  const save = async () => {
    setBusy(true);
    try { await api.patch("/leadsources/settings", { freelancer_membership_cost: Number(m) }); toast.success("Membership cost saved"); onChange(); }
    catch { toast.error("Could not save"); } finally { setBusy(false); }
  };
  const channels = data?.channels || [];
  return (
    <div className="hud-card rounded-lg p-5" data-testid="cost-per-win-card">
      <div className="flex items-center gap-2 mb-4">
        <Coins className="w-4 h-4 text-amber-400" />
        <span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">Cost-Per-Win by channel</span>
        {data?.cheapest && <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 font-mono text-[10px]"><Trophy className="w-3 h-3" /> cheapest: {data.cheapest}</span>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[9px] font-mono uppercase tracking-wider text-slate-500 text-left border-b border-slate-800">
              <th className="py-2 pr-3">Channel</th><th className="py-2 pr-3">Wins</th><th className="py-2 pr-3">Spend</th><th className="py-2 pr-3">Cost / win</th><th className="py-2">Basis</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {channels.map((c) => (
              <tr key={c.channel} data-testid={`cpw-row-${c.channel.replace(/\s+/g, "-")}`}>
                <td className="py-2.5 pr-3 text-slate-200 font-medium">{c.channel}{data?.cheapest === c.channel && <Trophy className="inline w-3 h-3 text-emerald-400 ml-1" />}</td>
                <td className="py-2.5 pr-3 text-slate-300 font-mono">{c.wins}{c.wins_all_time != null ? <span className="text-slate-600"> / {c.wins_all_time} all-time</span> : ""}</td>
                <td className="py-2.5 pr-3 text-slate-300 font-mono">${c.spend}</td>
                <td className={`py-2.5 pr-3 font-mono font-bold ${c.cost_per_win == null ? "text-slate-600" : "text-cyan-300"}`}>{c.cost_per_win == null ? "—" : `$${c.cost_per_win}`}</td>
                <td className="py-2.5 text-[10px] text-slate-500">{c.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex items-end gap-2 flex-wrap">
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Freelancer membership / mo ($)</label>
          <input data-testid="membership-cost-input" type="number" min="0" step="0.01" value={m} onChange={(e) => setM(e.target.value)} className="w-44 bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none" />
        </div>
        <button data-testid="membership-save-btn" onClick={save} disabled={busy} className="flex items-center gap-2 px-4 py-2 rounded-md bg-cyan-500 text-[#080B11] font-bold text-sm hover:bg-cyan-400 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
        </button>
        <span className="text-[10px] text-slate-500 ml-1">Cost/win for Freelancer = membership ÷ awards this month. Ad channels = spend ÷ closes.</span>
      </div>
    </div>
  );
}

function SponsorApprovals({ sponsors, onChange }) {
  const [busyId, setBusyId] = useState(null);
  const resolve = async (id, approve) => {
    setBusyId(id + (approve ? "-a" : "-r"));
    try {
      const r = await api.post(`/leadsources/sponsors/${id}/resolve`, { approve, note: "" });
      toast.success(approve ? "Bid sponsored — top-of-list visibility" : "Sponsor request dismissed");
      onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not resolve"); }
    finally { setBusyId(null); }
  };
  return (
    <div className="hud-card rounded-lg p-5" data-testid="sponsor-approvals-card">
      <div className="flex items-center gap-2 mb-1"><Rocket className="w-4 h-4 text-amber-400" /><span className="font-mono text-[11px] uppercase tracking-wider text-amber-400/90">Sponsor approvals</span><span className="px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-400 font-mono text-[10px]">{sponsors.length} pending</span></div>
      <p className="text-[10px] text-slate-500 mb-3">High-value projects your Prospector bid on. Approve to pay for top-of-list visibility (spends money on your Freelancer account).</p>
      <div className="space-y-2">
        {sponsors.map((s) => (
          <div key={s.id} data-testid={`sponsor-row-${s.id}`} className="flex items-center justify-between gap-3 rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2">
            <div className="min-w-0">
              <div className="text-sm text-slate-200 truncate">{s.title}</div>
              <div className="text-[10px] font-mono text-slate-500">{s.currency} {s.budget} · score {s.score} {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="text-cyan-400 inline-flex items-center gap-0.5">view <ExternalLink className="w-2.5 h-2.5" /></a>}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid={`sponsor-approve-${s.id}`} onClick={() => resolve(s.id, true)} disabled={busyId} className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-amber-500 text-[#080B11] font-bold text-xs hover:bg-amber-400 disabled:opacity-50">{busyId === s.id + "-a" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Crown className="w-3 h-3" />} Sponsor</button>
              <button data-testid={`sponsor-reject-${s.id}`} onClick={() => resolve(s.id, false)} disabled={busyId} className="px-3 py-1.5 rounded-md border border-slate-700 text-slate-400 text-xs hover:text-slate-200 disabled:opacity-50">Skip</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProposalLearning({ stats }) {
  const styles = Object.entries(stats || {});
  if (!styles.length) return null;
  const best = styles.reduce((a, b) => ((b[1]?.awards || 0) > (a[1]?.awards || 0) ? b : a), styles[0]);
  return (
    <div className="mt-3 pt-3 border-t border-slate-800/60 flex items-center gap-2 flex-wrap" data-testid="proposal-learning">
      <FlaskConical className="w-3.5 h-3.5 text-violet-400" />
      <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Proposal A/B</span>
      {styles.map(([name, s]) => (
        <span key={name} className={`px-1.5 py-0.5 rounded text-[10px] font-mono border ${name === best[0] && (best[1]?.awards || 0) > 0 ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "bg-slate-700/30 text-slate-400 border-slate-700"}`}>
          {name}: {s?.awards || 0}/{s?.bids || 0} won{name === best[0] && (best[1]?.awards || 0) > 0 ? " ★" : ""}
        </span>
      ))}
    </div>
  );
}

function DeliveryList({ deliveries }) {
  return (
    <div className="hud-card rounded-lg overflow-hidden" data-testid="delivery-list">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-800"><PackageCheck className="w-4 h-4 text-emerald-400" /><span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">Awarded → in delivery</span></div>
      <div className="divide-y divide-slate-800/60">
        {deliveries.map((d) => (
          <div key={d.id} className="px-5 py-4" data-testid={`delivery-${d.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-slate-200">{d.title}</span>
              <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 text-[9px] font-mono">{(d.status || "in_progress").toUpperCase()}</span>
              {d.agent_name && <span className="text-[10px] font-mono text-slate-500">→ {d.agent_name}</span>}
            </div>
            {d.brief?.summary && <p className="text-[12px] text-slate-400 mt-1.5">{d.brief.summary}</p>}
            {d.brief?.stack?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {d.brief.stack.map((s, i) => <span key={i} className="px-1.5 py-0.5 rounded bg-slate-800/60 text-slate-400 text-[10px] font-mono">{s}</span>)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function BidList({ bids }) {
  return (
    <div className="hud-card rounded-lg overflow-hidden" data-testid="bid-list">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-800"><Gavel className="w-4 h-4 text-cyan-400" /><span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">Recent bids</span></div>
      {bids.length === 0 ? (
        <div className="p-10 text-center text-slate-500 text-sm">No bids yet. Connect Freelancer and hit "Prospect now".</div>
      ) : (
        <div className="divide-y divide-slate-800/60">
          {bids.map((b) => (
            <div key={b.id} className="px-5 py-3.5 flex items-center gap-4" data-testid={`bid-${b.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-200 truncate flex items-center gap-1.5">
                  {b.title}
                  {b.sponsored && <Crown className="w-3 h-3 text-amber-400 shrink-0" title="Sponsored" />}
                </div>
                <div className="text-[10px] font-mono text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                  <span>by {b.agent_name} · {new Date(b.created_at).toLocaleString()}{b.price_note ? ` · ${b.price_note}` : ""}</span>
                  {typeof b.score === "number" && <span className="px-1 py-0.5 rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/30">score {b.score}</span>}
                  {b.client_verified && <span className="px-1 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 inline-flex items-center gap-0.5"><ShieldCheck className="w-2.5 h-2.5" /> verified</span>}
                </div>
              </div>
              <div className="text-right shrink-0"><div className="text-[9px] font-mono uppercase text-slate-500">Bid</div><div className="font-mono text-sm text-emerald-400">{b.currency} {money(b.amount).replace("$", "")}</div></div>
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono shrink-0 border ${b.award_status === "awarded" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-slate-600/20 text-slate-400 border-slate-600/40"}`}>{b.award_status === "awarded" ? "🎉 AWARDED" : "SUBMITTED"}</span>
              {b.url && <a href={b.url} target="_blank" rel="noreferrer" className="p-1.5 rounded-md border border-slate-700 text-slate-400 hover:text-cyan-400 shrink-0"><ExternalLink className="w-3 h-3" /></a>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
